import { forwardRef, useMemo, useState } from 'react'
import type { JSX } from 'react'
import { useLocation } from 'react-router-dom'

import { BookmarkIcon, IconButton } from '@buffer-mono/popcorn'

import {
  captureFilters,
  deriveSectionFromRoute,
  deriveViewFromRoute,
  findMatchingSavedView,
  hasCapturedFilters,
} from '~publish/helpers/savedViews'

import { DeleteViewAlertDialog } from './DeleteViewAlertDialog'
import { useSavedViewsQuery } from './hooks'
import { SaveViewDialog } from './SaveViewDialog'

import styles from './SaveViewButton.module.css'

/**
 * Filled bookmark counterpart to Popcorn's outline `BookmarkIcon`. Popcorn
 * currently ships only the outline variant, so we mirror its SVG structure
 * here (same `viewBox`, classes, size/stroke wiring) and swap the path to a
 * solid fill to preserve the icon-system styling contract without touching
 * Popcorn source. Remove this local copy once Popcorn exports a filled one.
 */
const BookmarkFilledIcon = forwardRef<
  SVGSVGElement,
  React.SVGAttributes<SVGElement>
>(({ className, ...props }, ref) => {
  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      data-icon="bookmark-filled"
      aria-hidden="true"
      width={16}
      height={16}
      ref={ref}
      {...props}
    >
      <path
        d="M19 21L12 17L5 21V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H17C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5V21Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
})
BookmarkFilledIcon.displayName = 'BookmarkFilledIcon'

/**
 * Page-header icon button that saves or unsaves the current view. When the
 * current route + filters match an existing saved view it renders a filled
 * bookmark and opens a delete confirmation; otherwise it shows the outline
 * bookmark and opens the create dialog. Hidden when the current route has
 * nothing to save (no view slug and no captured filter, or a URL whose
 * filters can't be captured).
 */
export function SaveViewButton(): JSX.Element | null {
  const { pathname, search } = useLocation()
  const { savedViews, loading } = useSavedViewsQuery()
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const { canSave, matchingView } = useMemo(() => {
    const searchParams = new URLSearchParams(search)
    const section = deriveSectionFromRoute(pathname)
    const view = deriveViewFromRoute(pathname, searchParams)
    const capture = captureFilters(section, pathname, searchParams)
    // Invalid filter shape: don't claim a stored view matches (they're all
    // canonical). The save button itself is also hidden in this state — see
    // the visibility gate below.
    const matched = capture.ok
      ? findMatchingSavedView(savedViews, section, view, capture.filters)
      : undefined
    return {
      // CORE-5334: a view is saveable on its own once it has something to
      // identify it — either a derived view slug (the tab/section selection,
      // e.g. Drafts or Calendar) or a captured filter. This lets users
      // bookmark tab-only views like "Drafts / All Channels" without adding a
      // throwaway filter. Sections with a view selector (publish, community,
      // create) are always saveable; insights has no view slug, so it falls
      // back to requiring a captured filter (a channel or date range).
      // `capture.ok` still gates everything so a malformed URL can't dead-end
      // the save dialog.
      canSave:
        capture.ok && (view !== null || hasCapturedFilters(capture.filters)),
      matchingView: matched,
    }
  }, [pathname, search, savedViews])

  // Avoid a cold-load flicker: on first navigation to a saved-view URL the
  // query resolves asynchronously, so rendering the unfilled "Save" state
  // eagerly would flip to "Remove" a tick later. A double-click during that
  // window would also create a duplicate. Keep the button hidden until we
  // have cached data to compare against. On warm refetches `savedViews` is
  // already populated, so we don't flicker the button off.
  if (loading && savedViews.length === 0) return null
  // Hide only when there's nothing to act on: the current view isn't saveable
  // (no view slug and no captured filter, or a malformed URL whose filters
  // can't be captured) and it doesn't match an existing saved view. The
  // matched override ensures a filter-less saved view can still be removed
  // from its page even when `canSave` is false.
  if (!canSave && !matchingView) return null

  if (matchingView) {
    return (
      <>
        <IconButton
          variant="tertiary"
          size="small"
          label="Remove saved view"
          tooltip="Remove saved view"
          className={styles.activeButton}
          onClick={(): void => setDeleteDialogOpen(true)}
        >
          <BookmarkFilledIcon className={styles.activeBookmark} />
        </IconButton>
        <DeleteViewAlertDialog
          view={matchingView}
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
        />
      </>
    )
  }

  return (
    <>
      <IconButton
        variant="tertiary"
        size="small"
        label="Save current view"
        tooltip="Save current view"
        onClick={(): void => setSaveDialogOpen(true)}
      >
        <BookmarkIcon />
      </IconButton>
      <SaveViewDialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen} />
    </>
  )
}
